import { useState, useCallback, MutableRef, useRef } from 'preact/hooks';

import { keyExtractor, getPrevNextKey } from '../utils/PRIVATE_collectionUtils';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useCollectionFocusRing } from '../hooks/PRIVATE_useCollectionFocusRing';
import { useCurrentKey } from '../hooks/PRIVATE_useCurrentKey';
import { ITEM_SELECTOR } from './useNavigationListItem';

import { NavigationListContextProps } from './NavigationListContext';

/**
 * type for payload of current key change event handler
 */
type CurrentKeyDetail<K> = {
  value: K;
};

/*TODO: JET-58534. Create a hook to share some logic between navlist and tabbar. Hook will be used inside this one. */
export function useNavigationList<K extends string | number>({
  containerRef,
  selection
}: {
  containerRef: MutableRef<HTMLUListElement>;
  selection?: K;
}) {
  //Ref used to track if the first item was already rendered
  const firstItemRenderedKey = useRef<K>();
  const [currentKey, setCurrentKey] = useState<K | undefined>(selection);

  const onCurrentKeyChange = useCallback(({ value }: CurrentKeyDetail<K | undefined>) => {
    setCurrentKey(value);
  }, []) as NavigationListContextProps<K>['onCurrentKeyChange'];

  const { currentKeyProps } = useCurrentKey(
    (element) => keyExtractor(element, ITEM_SELECTOR),
    false,
    getPrevNextKey(containerRef.current, currentKey, true, ITEM_SELECTOR),
    getPrevNextKey(containerRef.current, currentKey, false, ITEM_SELECTOR),
    undefined,
    undefined,
    currentKey,
    onCurrentKeyChange
  );

  const [showFocusRing, focusRingProps] = useCollectionFocusRing(
    (elem: Element | null) => {
      return !!containerRef.current?.contains(elem);
    },
    ['ArrowUp', 'ArrowDown']
  );

  const containsActiveElement = useCallback(() => {
    return !!containerRef?.current?.contains(document.activeElement);
  }, []);

  return {
    firstItemRenderedKey,
    showFocusRing,
    currentKey,
    onCurrentKeyChange,
    containsActiveElement,
    containerHandlers: mergeProps(focusRingProps, currentKeyProps)
  };
}
